import { IOffense, IOffenderVictim } from '@/models';
import { MasterNameType } from '@/models/master.names';

import {
  IMissingFields,
  INIBRSValidationRule,
  IRenderSection,
  ISubSection,
  IValidationSection,
} from './data';

export const handleReportClasses = (
  inputName: string,
  fieldKey: string | null,
): string => {
  const invalidField = fieldKey?.split('.').pop();
  return invalidField && invalidField === inputName ? 'reportRequired' : '';
};

export type parseLinkParams = INIBRSValidationRule & {
  kaseId: string;
  itemId: string;
  offenseId?: string;
  offenderId?: string;
  role?: 'offender' | 'victim';
};
export const parseLink = ({
  optionLocationForm,
  itemId,
  key,
  kaseId,
  optionScreen,
  offenseId = undefined,
  offenderId = undefined,
  role = undefined,
}: parseLinkParams) => {
  return `/cases/${kaseId}/${optionLocationForm}?fieldKey=${key}&id=${itemId}${
    optionScreen ? `&screen=${optionScreen}` : ''
  }${offenseId ? `&offenseId=${offenseId}` : ''}${
    offenderId ? `&offenderId=${offenderId}` : ''
  }${role ? `&role=${role}` : ''}`;
};

export const validateFields =
  (dataRow: any) =>
  ({
    key,
    label,
    optionLocationForm,
    id,
    optionScreen,
    shouldValidate,
  }: INIBRSValidationRule) => {
    const dataKey = key.split('.').reduce((acc, k) => acc?.[k], dataRow);
    if (shouldValidate && shouldValidate !== '') {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const validateTest = new Function('dataRow', `return ${shouldValidate}`);
      if (!validateTest(dataRow)) {
        return;
      }
    }
    if (!dataKey?.toString().length && dataRow?._id) {
      return {
        label,
        optionLocationForm,
        itemId: id
          ? id.split('.').reduce((acc, idKey) => acc?.[idKey], dataRow)
          : dataRow?._id,
        key,
        optionScreen,
      };
    }
  };

export const runReportValidation = async (
  kaseId: string | undefined,
  NibrsReportValidation: IValidationSection[],
  itemFetchingMethods: any,
) => {
  let allInvalidItems: IRenderSection[] = [];
  if (!kaseId) {
    // eslint-disable-next-line no-console
    console.error('Invalid CaseId');
    return;
  }
  for (const validationSection of NibrsReportValidation) {
    const fetchMethod = itemFetchingMethods.get(validationSection.sectioName);
    if (!fetchMethod) {
      // eslint-disable-next-line no-console
      console.error(
        `missing fetch method for section ${validationSection.sectioName}`,
      );
      return;
    }
    let itemsToValidate;
    try {
      itemsToValidate = await fetchMethod(kaseId, {
        limit: 1000,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`fetch failed for ${fetchMethod.toString()}`);
    }
    const allItems = validationSection?.paginated
      ? [...itemsToValidate.data[validationSection.paginated]]
      : Array.isArray(itemsToValidate.data)
      ? itemsToValidate.data
      : [itemsToValidate.data];

    if (validationSection.validateWhenExists && !allItems?.length) {
      continue;
    }

    if (!allItems?.length) {
      allInvalidItems = [
        ...allInvalidItems,
        {
          categoryName: validationSection.label,
          emptyCategory: true,
        },
      ];
    } else {
      let subSections: Array<ISubSection> = [];
      for (const dataRow of allItems) {
        if (dataRow?.masterName && dataRow.masterName?.isUnknown) {
          continue;
        }
        const validationFunction = validateFields(dataRow);
        const invalidItems = validationSection.validationRules.reduce(
          (acc, item) => {
            const invalidItem = validationFunction(item);
            return invalidItem ? [...acc, invalidItem] : acc;
          },
          [] as IMissingFields[],
        );
        if (Array.isArray(invalidItems) && invalidItems?.length) {
          subSections = [
            ...subSections,
            {
              subTitle: `${dataRow.number} ${
                dataRow?.fullName || dataRow?.name || ''
              }`,
              missingFields: invalidItems,
            },
          ];
        }
      }

      if (subSections?.length) {
        allInvalidItems = [
          ...allInvalidItems,
          {
            categoryName: validationSection.label,
            subSections,
          },
        ];
      }
    }
  }
  return allInvalidItems;
};

export const qualifiesAsKnownOffender = (
  offenderVictim: IOffenderVictim,
): boolean => {
  return !!offenderVictim.offender;
};

export const allOffensesValid = (
  offenses: IOffense[],
  NibrsOffenseValidation: INIBRSValidationRule[],
  NibrsOffenderValidation: INIBRSValidationRule[],
  NibrsVictimValidations: INIBRSValidationRule[],
) => {
  if (!offenses || !offenses?.length) return false;
  let areOffensesValid = true;
  for (const offense of offenses) {
    if (offense.penalCode?.ibrCode === '999') {
      continue;
    }

    const hasKnownOffender = offense.offenderVictims.some(
      qualifiesAsKnownOffender,
    );
    if (!hasKnownOffender) {
      areOffensesValid = false;
      break;
    }

    for (const offenseRule of NibrsOffenseValidation) {
      const validationFunction = validateFields(offense);
      if (validationFunction(offenseRule)) {
        areOffensesValid = false;
        break;
      }

      for (const offenderVictim of offense.offenderVictims) {
        if (
          !offenderVictim.offender?.isUnknown &&
          (offenderVictim.offender as any)?.type !== MasterNameType.ORGANIZATION
        ) {
          for (const personRule of NibrsOffenderValidation) {
            const offenderValidationFunction = validateFields(
              offenderVictim.offender,
            );
            if (offenderValidationFunction(personRule)) {
              areOffensesValid = false;
              break;
            }
          }
        }

        for (const victimItem of offenderVictim.victims) {
          if (!victimItem?.victim?.isUnknown) {
            for (const victimRule of NibrsVictimValidations) {
              const victimValidationFunction = validateFields(
                victimItem.victim,
              );
              if (victimValidationFunction(victimRule)) {
                areOffensesValid = false;
                break;
              }
            }
          } // end of loop offenderVictim
        }
      } // end of loop offenderVictim
    }
  }
  return areOffensesValid;
};
